/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Search component.
 * @module components/theme/Search/Search
 * - removed <div className="tileFooter"> from the page
 * - translated searchResultsTitle
 * - Helmet title translated
 * - read correct data from search state (total and batching)
 * - used siteSearchContent instead of searchContent action from @plone/volto
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { UniversalLink } from '@plone/volto/components';
import { FormattedMessage } from 'react-intl';
import { Portal } from 'react-portal';
import {
  Container,
  Pagination,
  Button,
  Header,
  Input,
  Loader,
  GridRow,
  GridColumn,
  Grid,
} from 'semantic-ui-react';
import { ListingImage, PriceRangeIndications } from '@package/components';
import TagsView from '@package/components/PreviewItems/TagsView';
import { useDebouncedEffect } from '@package/helpers/debounce';
import config from '@plone/volto/registry';
import { Helmet } from '@plone/volto/helpers';
import {
  siteSearchContent,
  resetSiteSearchContent,
} from '@package/actions/siteSearchContent';
import PreviewItemDates from '@package/components/PreviewItems/PreviewItemDates';
import ItemDescription from '@package/components/PreviewItems/ItemDescription';
import { SearchTags, Toolbar, Icon } from '@plone/volto/components';
import { defineMessages, useIntl } from 'react-intl';

import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';

const messages = defineMessages({
  searchResultsTitle: {
    id: 'searchResultsTitle',
    defaultMessage: 'Results for: {search} - Search',
  },
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  Esperienza: {
    id: 'Esperienza',
    defaultMessage: 'Esperienza',
  },
  Event: { id: 'Event', defaultMessage: 'Evento' },
  Itinerario: {
    id: 'Itinerario',
    defaultMessage: 'Itinerario',
  },
  NewsItem: {
    id: 'News Item',
    defaultMessage: 'Notizia',
  },
  SchedaTematica: {
    id: 'SchedaTematica',
    defaultMessage: 'Scheda Tematica',
  },
  Storia: { id: 'Storia', defaultMessage: 'Storia' },
  Document: {
    id: 'Document',
    defaultMessage: 'Pagina',
  },
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/**
 * Search class.
 * @class SearchComponent
 * @extends Component
 */
const Search = ({ pathname }) => {
  const intl = useIntl();
  const history = useHistory();
  const searchParams = useQuery();

  const [isClient, setIsClient] = useState(false);
  const [filters, setFilters] = useState({ currentPage: 1 });
  const search = useSelector((state) => state.search);

  const dispatch = useDispatch();

  const setSearchParams = (options) => {
    const params = new URLSearchParams(options);
    history.push({ search: params.toString() });
  };

  //on load
  useEffect(() => {
    dispatch(resetSiteSearchContent());

    const params = Object.fromEntries(searchParams);
    if (params.b_start) {
      params.currentPage =
        Math.floor(params.b_start / config.settings.defaultPageSize) + 1;
      delete params.b_start;
    }
    setFilters(params);
    setIsClient(true);
  }, []);

  React.useEffect(() => {
    if (isClient) {
      let portal = document.getElementById('leadimage-portal');
      if (portal) {
        portal.remove();
      }
    }
  }, [isClient]);

  useDebouncedEffect(
    () => {
      //if (filters.SearchableText || filters.portal_type) {
      return doSearch();
      //}
      //return null;
    },
    600,
    [filters],
  );

  const doSearch = () => {
    const options = { ...filters };
    const page = options.currentPage || 1;
    if (page > 1) {
      options.b_start = (page - 1) * config.settings.defaultPageSize;
    }
    setSearchParams(options);

    options['use_site_search_settings'] = 1;
    dispatch(siteSearchContent('', options));
  };

  const handleQueryPaginationChange = (e, { activePage }) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setFilters({ ...filters, currentPage: activePage });
  };

  const onSortChange = (event, sort_order) => {
    const sort_on = event.target.name;
    const _filters = {
      ...filters,
      sort_on,
      sort_order: sort_order ?? 'ascending',
    };
    if (sort_on === 'relevance') {
      delete _filters.sort_on;
      delete _filters.sort_order;
    }
    setFilters({ ..._filters });
  };

  const PORTAL_TYPES = {
    Event: intl.formatMessage(messages.Event),
    Esperienza: intl.formatMessage(messages.Esperienza),
    SchedaTematica: intl.formatMessage(messages.SchedaTematica),
    Itinerario: intl.formatMessage(messages.Itinerario),
    Document: intl.formatMessage(messages.Document),
    Storia: intl.formatMessage(messages.Storia),
    //    'News Item': intl.formatMessage(messages.NewsItem),
  };
  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */

  return (
    <Container id="page-search">
      <Helmet
        title={intl.formatMessage(messages.searchResultsTitle, {
          search: filters.SearchableText ?? '',
        })}
      />

      <div className="container">
        <article id="content">
          <header>
            {!search.loading && search.loaded && (
              <h1 className="documentFirstHeading" aria-live="polite">
                {filters.SearchableText ? (
                  <>
                    <FormattedMessage
                      id="{num} results for"
                      defaultMessage="{num} results for"
                      values={{
                        num: search.total,
                      }}
                    />
                    :
                  </>
                ) : (
                  <>
                    <FormattedMessage
                      id="{num} results found"
                      defaultMessage="{num} results found"
                      values={{
                        num: search.total,
                      }}
                    />
                  </>
                )}
              </h1>
            )}
            <form>
              <Input
                aria-label={intl.formatMessage(messages.search)}
                onChange={(e, { value }) => {
                  setFilters({
                    ...filters,
                    SearchableText: value,
                    currentPage: 1,
                  });
                }}
                name="SearchableText"
                value={filters.SearchableText ?? ''}
                transparent
                placeholder={intl.formatMessage(messages.search)}
                title={intl.formatMessage(messages.search)}
                aria-controls="content"
                icon="search"
              />
              <div className="search-portal-types">
                {Object.entries(PORTAL_TYPES).map(([key, label]) => {
                  const active = filters.portal_type === key;
                  return (
                    <Button
                      size="mini"
                      key={key}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const _filters = { ...filters, currentPage: 1 };
                        if (active) {
                          delete _filters.portal_type;
                        } else {
                          _filters.portal_type = key;
                        }
                        setFilters({
                          ..._filters,
                        });
                      }}
                      active={active}
                      disabled={search.loading}
                      aria-controls="content"
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </form>

            <SearchTags />

            {search.total > 0 && !search.loading ? (
              <div className="items_total sort-widget">
                <Header>
                  <Header.Content className="header-content">
                    <div className="sort-by">
                      <FormattedMessage
                        id="Sort By:"
                        defaultMessage="Sort by:"
                      />
                    </div>
                    <Button
                      onClick={(event) => {
                        onSortChange(event);
                      }}
                      name="relevance"
                      size="tiny"
                      active={
                        filters.sort_on === 'relevance' || !filters.sort_on
                      }
                      className="button-sort"
                    >
                      <FormattedMessage
                        id="Relevance"
                        defaultMessage="Relevance"
                      />
                    </Button>
                    <Button
                      onClick={(event) => {
                        onSortChange(event);
                      }}
                      name="sortable_title"
                      size="tiny"
                      active={filters.sort_on === 'sortable_title'}
                      className="button-sort"
                    >
                      <FormattedMessage
                        id="Alphabetically"
                        defaultMessage="Alphabetically"
                      />
                    </Button>
                    <Button
                      onClick={(event) => {
                        onSortChange(event, 'reverse');
                      }}
                      name="effective"
                      size="tiny"
                      className="button-sort"
                      active={filters.sort_on === 'effective'}
                    >
                      <FormattedMessage
                        id="Date (newest first)"
                        defaultMessage="Date (newest first)"
                      />
                    </Button>
                  </Header.Content>
                </Header>
              </div>
            ) : !search.loading && search.loaded ? (
              <div>
                <FormattedMessage
                  id="No results found"
                  defaultMessage="No results found"
                />
              </div>
            ) : (
              <></>
            )}
            {search.loading && (
              <Loader
                active
                inline="centered"
                style={{ marginBottom: '4rem' }}
              />
            )}
          </header>
          {!search.loading && search.loaded && (
            <section id="content-core">
              <Grid className="listing">
                <GridRow>
                  {search.items.map((item) => {
                    return (
                      <GridColumn
                        mobile="16"
                        tablet="6"
                        computer="4"
                        key={item['@id']}
                      >
                        <article className="result-item item">
                          <UniversalLink
                            item={item}
                            className="image-wrapper url"
                            title={item['@type']}
                          >
                            <ListingImage item={item} />
                            {item.thematic_area && (
                              <TagsView subjects={[item.thematic_area]} />
                            )}
                            <PriceRangeIndications item={item} />
                          </UniversalLink>
                          <div className="result-item-content">
                            {item.type_title?.toUpperCase() !== 'PAGINA' && (
                              <div className="type">{item.type_title}</div>
                            )}
                            <h2 className="tileHeadline">
                              <UniversalLink
                                item={item}
                                className="summary url"
                                title={item['@type']}
                              >
                                {item.title}
                              </UniversalLink>
                            </h2>
                            {item['@type'] !== 'Esperienza' && (
                              <PreviewItemDates item={item} />
                            )}
                            <ItemDescription item={item} />
                          </div>
                        </article>
                      </GridColumn>
                    );
                  })}
                </GridRow>
              </Grid>
              {search.batching &&
                (search.batching.prev || search.batching.next) && (
                  <div className="search-footer">
                    <Pagination
                      activePage={filters.currentPage}
                      totalPages={Math.ceil(
                        search.total / config.settings.defaultPageSize,
                      )}
                      onPageChange={handleQueryPaginationChange}
                      firstItem={null}
                      lastItem={null}
                      prevItem={{
                        content: <Icon name={paginationLeftSVG} size="18px" />,
                        icon: true,
                        'aria-disabled': !search.batching.prev,
                        className: !search.batching.prev ? 'disabled' : null,
                      }}
                      nextItem={{
                        content: <Icon name={paginationRightSVG} size="18px" />,
                        icon: true,
                        'aria-disabled': !search.batching.next,
                        className: !search.batching.next ? 'disabled' : null,
                      }}
                    />
                  </div>
                )}
            </section>
          )}
        </article>
      </div>
      {isClient && (
        <>
          <Portal node={__CLIENT__ && document.getElementById('toolbar')}>
            <Toolbar
              pathname={pathname}
              hideDefaultViewButtons
              inner={<span />}
            />
          </Portal>
        </>
      )}
    </Container>
  );
};

export default Search;
